import { Account } from '@/domain/usecases'
import { i18nTranslationHelper } from '@/i18n'
import { AccountStore } from '@/infra/tracking/account-store'
import {
  makeRemoteAccountProfile,
  makeRemoteUserProfileAssessment
} from '@/main/factories/usecases'
import { redirectToLogin } from '@/data/services/login/login-service-utils'
import { FeatureFlagClient } from '@/infra/feature-flag'
import { enrichAccountProfileContext } from '@/presentation/helpers/account-profile-helper'
import errorMonitor from '@/infra/tracking/error-monitor'
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useRef,
  useState
} from 'react'
import { useQueryClient } from 'react-query'
import { AnalyticsTracker } from '@/infra/tracking'
import { useLDClient } from 'launchdarkly-react-client-sdk'
// import { loginService } from '@/data/services/login/login-service'
import { PathConstants } from '@/main/routes/paths'

type AccountProviderProps = {
  children: ReactNode
  initialState?: AccountContextValueType
}

export type HelpCenterConfigType = {
  companyId?: string
  redirectTo: string
  hasChatBot: boolean
  hasLink: boolean
  hasWhatsApp: boolean
  whatsappPhone?: string
  companyName?: string
}

export type AccountWithCustomProperties = Account.Model & {
  displayName: string
  helpCenterConfig: HelpCenterConfigType
}
type AccountContextValueType = AccountWithCustomProperties | null

export type AccountContextType =
  | null
  | [AccountContextValueType, Dispatch<SetStateAction<AccountContextValueType>>]

export type ValidAccountState = [
  AccountWithCustomProperties,
  Dispatch<SetStateAction<AccountWithCustomProperties>>
]

const AccountContext = createContext({} as AccountContextType)

const changeAppLanguageByUserPreference = (language: string) => {
  const currentLanguage = i18nTranslationHelper.getCurrentLanguage()
  const supportedLanguages = i18nTranslationHelper.getAllLanguages()

  if (!supportedLanguages.includes(language)) {
    return
  }

  if (currentLanguage === language) {
    return
  }

  i18nTranslationHelper.changeLanguage(language).then(() => {
    window.location.reload()
  })
}

const AccountProvider = ({ initialState = null, children }: AccountProviderProps) => {
  const [account, setAccount] = useState<AccountContextValueType>(initialState)
  const isAnonymous = PathConstants.isInStaticRoute()
  const queryClient = useQueryClient()
  const remoteAccountProfile = makeRemoteAccountProfile(queryClient)
  const remoteUserProfileAssessment = makeRemoteUserProfileAssessment(queryClient)
  const ldClient = useLDClient()
  const isInitialized = useRef(false)
  const isRedirecting = useRef(false)

  /**
   * Initializes all integrations (LaunchDarkly, Sentry, Analytics)
   * This runs regardless of whether data came from API or loader
   */
  const initializeIntegrations = async (accountData: Account.Model) => {
    if (!ldClient) {
      throw new Error('LaunchDarkly client not ready')
    }

    // Identify user in LaunchDarkly (calls waitForInitialization internally)
    await FeatureFlagClient.identifyUser(ldClient, accountData)

    // Identify user in Sentry for error tracking
    errorMonitor.identifyUser(accountData)

    // Identify user in Analytics (GA, GTM, etc.)
    AnalyticsTracker.identifyUser(accountData)
  }

  /**
   * Completes account setup after data is available
   * Enriches account, updates store, changes language, fetches assessment
   */
  const completeAccountSetup = async (accountData: Account.Model) => {
    const responseProfile = await remoteUserProfileAssessment.getLatest()
    const enrichedAccount = enrichAccountProfileContext(accountData, ldClient)

    const updatedAccount = {
      ...enrichedAccount,
      latestProfileAssessment: responseProfile || undefined
    }

    setAccount(updatedAccount)
    AccountStore.setCurrentAccount(updatedAccount)
    changeAppLanguageByUserPreference(updatedAccount.preferenceLanguage || 'pt')
  }

  useEffect(() => {
    // Prevent double initialization
    if (isInitialized.current) {
      return
    }

    const initializeAccount = async () => {
      // Sync FeatureFlagClient with LaunchDarkly (called once on app load)
      FeatureFlagClient.syncWithClient(ldClient)

      // const isLogged = await loginService.isLoggedUser()

      if (isAnonymous) {
        // LaunchDarkly is already initialized with anonymous context in main.tsx
        console.log('[AccountProvider] Setting anonymous account')
        const anonAccount = Account.getAnonymous()
        setAccount(anonAccount)
        AccountStore.setCurrentAccount(anonAccount)
        isInitialized.current = true
        return
      }

      // Wait for LaunchDarkly client to be ready for authenticated users
      if (!ldClient) {
        return
      }

      try {
        // Path A: Initial state provided from loader (AccountPreRender)
        if (initialState && !Account.isAnonymous(initialState)) {
          // Initialize integrations with the pre-loaded data
          await initializeIntegrations(initialState)

          // Complete setup (enrichment already done in AccountPreRender, but ensures consistency)
          await completeAccountSetup(initialState)

          isInitialized.current = true
          console.log('[AccountProvider] ✅ All services initialized successfully (from loader)')
          return
        }

        // Path B: No initial state, fetch from API
        if (!account) {
          const data = await remoteAccountProfile.loaderGet()

          // Initialize integrations
          await initializeIntegrations(data)

          // Complete setup
          await completeAccountSetup(data)

          isInitialized.current = true
          console.log('[AccountProvider] ✅ All services initialized successfully (from API)')
        }
      } catch (error) {
        // Prevent multiple redirects
        if (isRedirecting.current) {
          return
        }

        console.warn('[AccountProvider] Error initializing account, redirecting to login')
        isRedirecting.current = true

        setTimeout(() => {
          redirectToLogin(window.location.href)
        }, 100)
      }
    }

    initializeAccount()
  }, [account, ldClient, initialState])

  return <AccountContext.Provider value={[account, setAccount]}>{children}</AccountContext.Provider>
}

export { AccountContext, AccountProvider }
